import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axiosInstance from "../Utils/axiosInstance";
import { v4 as uuidv4 } from "uuid";
import { clearPnsFromUrl, getCallerUrl, getCookieValue, updateStorage } from "../Utils/utils";

const baseUrl = process.env.REACT_APP_BASE_URL;

const getErrorMessage = (errResponse) => {
  if (errResponse?.data !== undefined) {
    if (errResponse.data.fieldErrorItems?.length > 0) {
      return errResponse.data.fieldErrorItems
        .map((errItem) => `${errItem.message}`)
        .join("\n");
    }
    return errResponse.data.message;
  }
  return errResponse?.statusText;
};

export const initiatePayment = createAsyncThunk(
  "paymentInitiation/initiatePayment",
  async ({ token, payload }, { rejectWithValue }) => {
    if (!token) {
      return rejectWithValue({ message: "Missing checkout session token" });
    }
    try {
      const headers = {
        "X-Request-Id": uuidv4(),
        "X-Caller-Url": getCallerUrl(),
      };
      const sessionToken = getCookieValue('shopper_token') || localStorage.getItem("sessionToken");
      if (sessionToken) {
        headers.Authorization = `Session ${sessionToken}`;
      }

      const response = await axiosInstance.post(
        `${baseUrl}/checkout/sessions/${token}`,
        payload,
        { headers }
      );

      if (
        response.data?.status?.status === "Rejected" ||
        response.data?.status?.status === "Failed"
      ) {
        return rejectWithValue(response?.data);
      }

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response);
    }
  }
);

export const submitNativeOtp = createAsyncThunk(
  "paymentInitiation/submitNativeOtp",
  async ({ transactionId, otp }, { rejectWithValue }) => {
    try {
      const headers = {
        "X-Request-Id": uuidv4(),
        "X-Caller-Url": getCallerUrl(),
      };
      const sessionToken = getCookieValue('shopper_token') || localStorage.getItem("sessionToken");
      if (sessionToken) {
        headers.Authorization = `Session ${sessionToken}`;
      }

      const response = await axiosInstance.post(
        `${baseUrl}/checkout/sessions/transactions/${transactionId}/native-otps/submit`,
        { otpValue: otp },
        { headers }
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response);
    }
  }
);

export const resendNativeOtp = createAsyncThunk(
  "paymentInitiation/resendNativeOtp",
  async ({ transactionId }, { rejectWithValue }) => {
    try {
      const headers = {
        "X-Request-Id": uuidv4(),
        "X-Caller-Url": getCallerUrl(),
      };
      const sessionToken = getCookieValue('shopper_token') || localStorage.getItem("sessionToken");
      if (sessionToken) {
        headers.Authorization = `Session ${sessionToken}`;
      }

      const response = await axiosInstance.post(
        `${baseUrl}/checkout/sessions/transactions/${transactionId}/native-otps/resend`,
        {},
        { headers }
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response);
    }
  }
);

const postTransactionAction = async ({ token, transactionId, action }, { rejectWithValue }) => {
  if (!token || !transactionId) {
    return rejectWithValue({ message: `Cannot ${action} transaction: missing token or transactionId` });
  }
  try {
    const headers = {
      "X-Request-Id": uuidv4(),
      "X-Caller-Url": getCallerUrl(),
    };
    const sessionToken = getCookieValue('shopper_token') || localStorage.getItem("sessionToken");
    if (sessionToken) {
      headers.Authorization = `Session ${sessionToken}`;
    }

    const response = await axiosInstance.post(
      `${baseUrl}/checkout/sessions/${token}/transactions/${transactionId}/${action}`,
      {},
      { headers }
    );
    return response.data;
  } catch (error) {
    return rejectWithValue(error.response);
  }
};

// Native OTP "Cancel" confirmed by the shopper.
export const cancelTransaction = createAsyncThunk(
  "paymentInitiation/cancelTransaction",
  (args, thunkApi) => postTransactionAction({ ...args, action: "cancel" }, thunkApi)
);

// Native OTP page timer ran out.
export const expireTransaction = createAsyncThunk(
  "paymentInitiation/expireTransaction",
  (args, thunkApi) => postTransactionAction({ ...args, action: "expire" }, thunkApi)
);

export const retryPayment = createAsyncThunk("paymentInitiation/retryPayment",
  async ({ token, transactionId }, { rejectWithValue }) => {
    try {
      const headers = {
        "X-Request-Id": uuidv4(),
        "X-Caller-Url": getCallerUrl(),
      };
      const response = await axiosInstance.post(
        `${baseUrl}/checkout/sessions/${token}/transactions/${transactionId}/clones`,
        {},
        { headers }
      );

      if (
        response.data?.status?.status === "Rejected" ||
        response.data?.status?.status === "Failed"
      ) {
        return rejectWithValue(response?.data);
      }

      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const updateTransactionStatus = createAsyncThunk(
  "paymentInitiation/updateTransactionStatus",
  async ({ token, transactionId, status }, { rejectWithValue }) => {
    try {
      const headers = {
        "Content-Type": "application/json",
        "X-Request-Id": uuidv4(),
        "X-Caller-Url": getCallerUrl(),
      };

      const response = await axiosInstance.put(
        `${baseUrl}/checkout/sessions/${token}/transactions/${transactionId}/status`,
        { status },
        { headers }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response);
    }
  }
);
// paymentInitiationError and translated where it is shown.
export const RETRY_LIMIT_KEYS = {
  method: "RETRY_LIMIT_METHOD",
  order: "RETRY_LIMIT_ORDER",
  session: "RETRY_LIMIT_SESSION",
  card: "RETRY_LIMIT_CARD",
};

// BE_1815: payment method limit. BE_1816: order or checkout session limit, told apart by the API message.
const getRetryLimitType = (reasonCode, apiMessage = "") => {
  if (reasonCode === "BE_1815") return "method";
  if (reasonCode === "BE_1816") return /order/i.test(apiMessage) ? "order" : "session";
  return "";
};

export const getRetryLimitKey = (reasonCode, apiMessage) =>
  RETRY_LIMIT_KEYS[getRetryLimitType(reasonCode, apiMessage)];

const initialState = {
  paymentInitiationLoading: false,
  paymentInitiationError: "",
  paymentInitiationData: null,
  failedModal: false,
  paymentInitiationRejectedMsg: "",
  isPaymentRejectedOrFailed: false, // ✅ Added missing key
  isRetryLimitReached: false,
  retryLimitType: "",
  methodAttemptsLeft: {},
  retryPaymentLoading: false,
  retryModal: false,
  formattedFieldErrorItems: {},
  nativeOtpLoading: false,
  nativeOtpError: "",
  nativeOtpSubmitSuccess: false,
  nativeOtpResendLoading: false,
  nativeOtpResendError: "",
};

export const paymentInitiationSlice = createSlice({
  name: "paymentInitiation",
  initialState,
  reducers: {
    resetPaymentInitiation(state) {
      state.paymentInitiationError = "";
      state.paymentInitiationData = null;
      state.isPaymentRejectedOrFailed = false;
      state.isRetryLimitReached = false;
      state.retryLimitType = "";
      state.paymentInitiationRejectedMsg = "";
      state.nativeOtpError = "";
      state.nativeOtpLoading = false;
      state.nativeOtpSubmitSuccess = false;
      state.nativeOtpResendError = "";
      state.nativeOtpResendLoading = false;
    },
    setPaymentInitiationData(state, action) {
      state.paymentInitiationData = action.payload
    },
    showFailedModal(state, action) {
      state.failedModal = action.payload;
    },
    showRetryModal(state, action) {
      state.retryModal = action.payload;
    },
    // For flows that call the initiate API directly (wallets) instead of the
    // initiatePayment thunk: applies the retry limit from `{ reasonCode, message }`.
    showRetryLimitReached(state, action) {
      const { reasonCode, message } = action.payload || {};
      const type = getRetryLimitType(reasonCode, message);
      if (!type) return;
      state.paymentInitiationError = RETRY_LIMIT_KEYS[type];
      state.isRetryLimitReached = true;
      state.retryLimitType = type;
      state.failedModal = true;
    },
    resetPaymentInitiationLoading(state) {
      state.paymentInitiationLoading = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(initiatePayment.pending, (state) => {
        state.paymentInitiationLoading = true;
        state.formattedFieldErrorItems = {}
        state.isRetryLimitReached = false;
        state.retryLimitType = "";
      })
      .addCase(initiatePayment.fulfilled, (state, action) => {
        state.paymentInitiationData = action.payload;
        state.isPaymentRejectedOrFailed = false;
        state.paymentInitiationRejectedMsg = action.payload?.status?.reason;
        state.paymentInitiationError = "";

        const isUpiIntent = action.payload?.paymentMethod?.brand === "UpiIntent";
        const isApproved = action.payload?.status?.status?.toLowerCase() === "approved";

        if (!isUpiIntent || isApproved) {
          // setTimeout(() => {
          state.paymentInitiationLoading = false;
          // }, 1500);
        }

        updateStorage("session", "update", "t_id", action.payload.transactionId);
      })
      .addCase(initiatePayment.rejected, (state, action) => {
        state.paymentInitiationLoading = false;
        state.paymentInitiationData = null;
        state.isPaymentRejectedOrFailed = true;

        const errResponse = action.payload;
        const reasonCode = errResponse?.data?.reasonCode || errResponse?.status?.reasonCode;
        const reason = errResponse?.data?.message || errResponse?.status?.reason;

        const formattedFieldErrorItems = {}
        if (errResponse?.data?.fieldErrorItems) {
          errResponse.data.fieldErrorItems
            .forEach((errItem) => {
              formattedFieldErrorItems[errItem.fieldName] = errItem.message
            })
          state.formattedFieldErrorItems = formattedFieldErrorItems
        }

        let retryLimitType = getRetryLimitType(reasonCode, reason);
        // The card limit is per card, not per method: the shopper can try another card.
        const isCardAttempt = action.meta?.arg?.payload?.instrumentDetails?.type?.toLowerCase().startsWith("card");
        if (retryLimitType === "method" && isCardAttempt) retryLimitType = "card";
        state.retryLimitType = retryLimitType;
        state.isRetryLimitReached = !!retryLimitType;

        if (retryLimitType === "method") {
          state.methodAttemptsLeft = {};
          (errResponse?.data?.fieldErrorItems || []).forEach((errItem) => {
            if (errItem.fieldName !== "Card") {
              state.methodAttemptsLeft[errItem.fieldName] = Number(errItem.message);
            }
          });
        }

        if (retryLimitType) {
          state.paymentInitiationError = RETRY_LIMIT_KEYS[retryLimitType];
        } else if (reasonCode?.startsWith("UF")) {
          state.paymentInitiationError = reason;
        } else {
          state.paymentInitiationError = "";
        }
      });
    builder
      .addCase(submitNativeOtp.pending, (state) => {
        state.nativeOtpLoading = true;
        state.nativeOtpError = "";
      })
      .addCase(submitNativeOtp.fulfilled, (state, action) => {
        state.nativeOtpLoading = false;
        if (action.payload?.success === false) {
          state.nativeOtpError =
            action.payload?.errorMessage || "Incorrect OTP. Please try again.";
          return;
        }
        state.nativeOtpError = "";
        state.nativeOtpSubmitSuccess = true;
      })
      .addCase(submitNativeOtp.rejected, (state, action) => {
        state.nativeOtpLoading = false;
        state.nativeOtpError =
          action.payload?.data?.errorMessage ||
          action.payload?.data?.message ||
          "Incorrect OTP. Please try again.";
      });
    builder
      .addCase(resendNativeOtp.pending, (state) => {
        state.nativeOtpResendLoading = true;
        state.nativeOtpResendError = "";
        state.nativeOtpError = "";
      })
      .addCase(resendNativeOtp.fulfilled, (state, action) => {
        state.nativeOtpResendLoading = false;
        if (action.payload?.success === false) {
          state.nativeOtpResendError =
            action.payload?.errorMessage || "Couldn't resend OTP. Please try again.";
        }
      })
      .addCase(resendNativeOtp.rejected, (state, action) => {
        state.nativeOtpResendLoading = false;
        state.nativeOtpResendError =
          action.payload?.data?.errorMessage ||
          action.payload?.data?.message ||
          "Couldn't resend OTP. Please try again.";
      });
    builder
      .addCase(retryPayment.pending, (state) => {
        state.retryPaymentLoading = true;
        state.isRetryLimitReached = false;
      })
      .addCase(retryPayment.fulfilled, (state, action) => {
        if (action.payload?.status?.status === "Rejected" || action.payload?.status?.status === "Failed") {
          state.paymentInitiationData = null;
        } else {
          state.paymentInitiationData = action.payload
        }
        updateStorage("session", "update", "t_id", action.payload.transactionId);
        state.retryPaymentLoading = false;
        state.failedModal = false;
        clearPnsFromUrl();

      })
      .addCase(retryPayment.rejected, (state, action) => {
        state.retryModal = false;
        state.failedModal = true;
        state.retryPaymentLoading = false;
        state.paymentInitiationData = null;
        clearPnsFromUrl();
      });
  },
});

export const {
  resetPaymentInitiation,
  showFailedModal,
  showRetryModal,
  showRetryLimitReached,
  resetPaymentInitiationLoading,
  setPaymentInitiationData
} = paymentInitiationSlice.actions;

export default paymentInitiationSlice.reducer;


